import { supabase } from './supabaseClient';
import type { SubMetricConfig } from '../utils/dimensionScoring';

export type { SubMetricConfig } from '../utils/dimensionScoring';

/**
 * Controls how event recommendations are scored and ranked.
 * - 'deficit': surface activities that target dimensions where the resident scores lowest
 * - 'goals':   surface activities that align with the resident's active wellness goals
 */
export type RecommendationMode = 'deficit' | 'goals';

export interface ProgramSettings {
  id: string;
  facilityId: string;
  programName: string;
  programDescription?: string;
  primaryColor: string;
  secondaryColor: string;
  showProgressLeader: boolean;
  useEventRecommendations: boolean;
  /** Color wellness-wheel slices in shades of their parent category's color so the
   *  wheel visually matches the category legend. Default true. */
  useCategoryWheelColors: boolean;
  /** Show the My Goals tip suggesting a goal for the resident's least-active
   *  wellness area. Default true. */
  showBalanceNudge: boolean;
  externalAppId?: number; // CareDaily external application ID (e.g., 1001 for Moorings Park)
  defaultExternalRole?: number; // Default external user role: 0 = resident, 1 = related party, 2 = SSO
  recommendationMode?: RecommendationMode; // How to score event recommendations (default: 'deficit')
  // Facility-level sub-metric list applied to every wellness dimension for the
  // share-across-categories pie scoring (e.g. resident_activity, resident_goals).
  dimensionMetrics?: SubMetricConfig[];
  createdAt?: string;
  updatedAt?: string;
}

export class ProgramSettingsService {
  /**
   * Get program settings for a facility
   */
  static async getProgramSettings(facilityId: string): Promise<ProgramSettings> {
    if (process.env.NODE_ENV !== 'production') {
      console.log('🔍 Fetching program settings for facility:', facilityId);
    }

    const { data, error } = await supabase
      .from('program_settings')
      .select('id, facility_id, program_name, program_description, primary_color, secondary_color, show_progress_leader, use_event_recommendations, use_category_wheel_colors, show_balance_nudge, external_app_id, default_external_role, recommendation_mode, dimension_metrics, created_at, updated_at')
      .eq('facility_id', facilityId)
      .maybeSingle();

    if (error) {
      console.error('❌ Error fetching program settings:', error);
      console.log('📋 Returning default settings due to error');
      return {
        id: '',
        facilityId,
        programName: 'Successful Aging',
        programDescription: '',
        primaryColor: '#667eea',
        secondaryColor: '#764ba2',
        showProgressLeader: true,
        useEventRecommendations: false,
        useCategoryWheelColors: true,
        showBalanceNudge: true
      };
    }

    // If no data returned, use defaults
    if (!data) {
      console.log('📋 No program settings found, using defaults');
      return {
        id: '',
        facilityId,
        programName: 'Successful Aging',
        programDescription: '',
        primaryColor: '#667eea',
        secondaryColor: '#764ba2',
        showProgressLeader: true,
        useEventRecommendations: false,
        useCategoryWheelColors: true,
        showBalanceNudge: true
      };
    }

    if (process.env.NODE_ENV !== 'production') {
      console.log('✅ Retrieved program settings:', data);
    }

    return this.mapDbToSettings(data);
  }

  /**
   * Update program settings for a facility
   */
  static async updateProgramSettings(
    facilityId: string,
    updates: Partial<Omit<ProgramSettings, 'id' | 'facilityId' | 'createdAt' | 'updatedAt' | 'externalAppId'>> & { externalAppId?: number | null }
  ): Promise<ProgramSettings> {
    console.log('📝 Updating program settings for facility:', facilityId, updates);

    const updateData: any = {};
    if (updates.programName !== undefined) updateData.program_name = updates.programName;
    if (updates.programDescription !== undefined) updateData.program_description = updates.programDescription;
    if (updates.primaryColor !== undefined) updateData.primary_color = updates.primaryColor;
    if (updates.secondaryColor !== undefined) updateData.secondary_color = updates.secondaryColor;
    if (updates.showProgressLeader !== undefined) updateData.show_progress_leader = updates.showProgressLeader;
    if (updates.useEventRecommendations !== undefined) updateData.use_event_recommendations = updates.useEventRecommendations;
    if (updates.useCategoryWheelColors !== undefined) updateData.use_category_wheel_colors = updates.useCategoryWheelColors;
    if (updates.showBalanceNudge !== undefined) updateData.show_balance_nudge = updates.showBalanceNudge;
    if (updates.externalAppId !== undefined) updateData.external_app_id = updates.externalAppId;
    if (updates.defaultExternalRole !== undefined) updateData.default_external_role = updates.defaultExternalRole;
    if (updates.recommendationMode !== undefined) updateData.recommendation_mode = updates.recommendationMode;
    if (updates.dimensionMetrics !== undefined) updateData.dimension_metrics = updates.dimensionMetrics && updates.dimensionMetrics.length > 0 ? updates.dimensionMetrics : null;

    // Try to update first
    const { data: existingData } = await supabase
      .from('program_settings')
      .select('id')
      .eq('facility_id', facilityId)
      .single();

    if (existingData) {
      // Update existing
      const { data, error } = await supabase
        .from('program_settings')
        .update(updateData)
        .eq('facility_id', facilityId)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating program settings:', error);
        throw error;
      }

      console.log('✅ Program settings updated successfully');
      return this.mapDbToSettings(data);
    } else {
      // Create new
      const { data, error } = await supabase
        .from('program_settings')
        .insert([{
          facility_id: facilityId,
          ...updateData
        }])
        .select()
        .single();

      if (error) {
        console.error('❌ Error creating program settings:', error);
        throw error;
      }

      console.log('✅ Program settings created successfully');
      return this.mapDbToSettings(data);
    }
  }

  /**
   * Map database row to ProgramSettings
   */
  private static mapDbToSettings(row: any): ProgramSettings {
    return {
      id: row.id || '',
      facilityId: row.facility_id || '',
      programName: row.program_name || 'Successful Aging',
      programDescription: row.program_description || '',
      primaryColor: row.primary_color || '#667eea',
      secondaryColor: row.secondary_color || '#764ba2',
      showProgressLeader: row.show_progress_leader !== false, // Default to true
      useEventRecommendations: row.use_event_recommendations === true, // Default to false
      useCategoryWheelColors: row.use_category_wheel_colors !== false, // Default to true
      showBalanceNudge: row.show_balance_nudge !== false, // Default to true
      externalAppId: row.external_app_id ?? undefined,
      defaultExternalRole: row.default_external_role ?? 0, // Default to 0 (resident)
      recommendationMode: (row.recommendation_mode === 'goals' ? 'goals' : 'deficit') as RecommendationMode,
      dimensionMetrics: Array.isArray(row.dimension_metrics) ? row.dimension_metrics : undefined,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }
}

